
import { Component, Vue, Prop, Inject, Watch } from 'nuxt-property-decorator';
import {
  FormDto,
  QuoteDto,
  QuoteItem,
  FormEntryDto,
  EventDto,
  FormState,
  FormType,
  AmountType,
  formatMoney,
  PaymentMethod,
  TaxRules,
} from 'fourwaves-shared';
import { Alert, InputText, Collapsible, HeightTransition } from 'fourwaves-shared/components';
import { Validator } from 'vee-validate';

@Component({ components: { Alert, InputText, Collapsible, HeightTransition } })
export default class Summary extends Vue {
  @Prop({ required: true }) readonly event!: EventDto;
  @Prop({ required: true }) readonly form!: FormDto;
  @Prop({ required: true }) readonly paymentMethod!: PaymentMethod;
  @Prop({ default: null }) readonly quote!: QuoteDto | null;
  @Prop({ default: null }) readonly formEntry!: FormEntryDto | null;
  @Prop({ type: String, default: '' }) readonly blockingMessage!: string;
  @Prop({ type: Array, default: Array }) readonly priceDiscrepancies!: QuoteItem[];
  @Prop({ type: String, default: null }) readonly couponCode!: string | null;
  @Prop(Boolean) readonly isFree!: boolean;

  @Inject() $validator!: Validator;

  readonly PaymentMethod = PaymentMethod;

  isSubmitBlocked = false;
  unblockSubmitTimeout: NodeJS.Timeout | null = null;
  localCouponCode: string | null = null;

  get isOnBillingPage() {
    if (!this.$route?.name) return false;
    return this.$route.name.includes('billing');
  }

  get submitButtonText() {
    const hasWaitingList =
      !this.formEntry && this.form.type === FormType.Registration && this.form.state === FormState.FullWithWaitingList;

    if (hasWaitingList) return this.$t('form.summary.join_waitlist');

    if (!this.isOnBillingPage) {
      const hasPaidItems = this.quoteItems.length > 0 && !this.isFree;

      if (!hasPaidItems) return this.$t('form.summary.edit');
      if (hasPaidItems) return this.$t('form.summary.payment.confirm_and_pay');
    }

    if (!this.isPaymentOmitted) return this.$t('form.summary.payment.pay_now');

    return this.$t('form.summary.submit');
  }

  get isCreditCardPayment() {
    return [
      PaymentMethod.Stripe,
      PaymentMethod.Paysafe,
      PaymentMethod.TouchNet,
      PaymentMethod.GlobalPayments,
      PaymentMethod.PayPalCardFields,
    ].includes(this.paymentMethod);
  }

  get isPaymentOmitted() {
    return (
      this.paymentMethod === PaymentMethod.Invoicing ||
      this.paymentMethod === PaymentMethod.Void ||
      this.paymentMethod === PaymentMethod.ForwardPayment
    );
  }

  get isWarningVisible() {
    return this.form.type === FormType.Submission && this.event.isRegistrationMandatoryForSubmission && !this.formEntry;
  }

  get areQuoteItemsVisible() {
    return this.quote && this.quoteItems.length > 0 && this.paymentMethod !== PaymentMethod.Void;
  }

  get taxTransitionKey() {
    if (!this.quote) return 0;
    return this.quote.gst + this.quote.qst + this.quote.hst + this.quote.vat + this.quote.pst;
  }

  get paymentProviderNotice() {
    let notice = this.$t('form.summary.payment.payment_notice');
    if (this.event.paysafePublishableKey) {
      notice += ' Paysafe™';
    } else if (this.event.touchNetUPayUrl) {
      notice += ' TouchNet™';
    } else if (this.event.globalPaymentsAppId) {
      notice += ' Global Payments™';
    } else if (this.event.monerisTokenizationId) {
      notice += ' Moneris™';
    } else if (this.event.paypalMerchantId) {
      notice += ' PayPal™';
    } else {
      notice += ' Stripe™';
    }
    return notice;
  }

  get hasTaxes() {
    const hasSpecialCaseTaxes = this.quoteItems.some(
      i => i.taxRuleAsString === TaxRules.Vat0 || i.taxRuleAsString === TaxRules.VatReverseCharge0,
    );
    if (!this.quote) return false;
    if (hasSpecialCaseTaxes) return true;

    return !!(this.quote.gst || this.quote.qst || this.quote.hst || this.quote.vat || this.quote.pst);
  }

  get quoteItems(): QuoteItem[] {
    return this.quote ? this.quote.quoteItems : [];
  }

  get quoteCouponCodeText(): string {
    if (!this.quote?.couponCode) return '';
    if (this.quote.couponCodeAmountType === AmountType.Percentage) {
      return `-${this.quote.couponCodeAmount}%`;
    }
    return `-${formatMoney(this.quote.couponCodeAmount!, this.event.currency)}`;
  }

  get isSubmitButtonDisabled() {
    return this.$wait.is('upload-*');
  }

  get withPayPalButtons() {
    return this.paymentMethod === PaymentMethod.PayPalButtons && this.quote && this.quote.total > 0;
  }

  beforeDestroy() {
    clearTimeout(this.unblockSubmitTimeout!);
  }

  @Watch('couponCode', { immediate: true })
  onCouponCodeChanged(couponCode: string | null) {
    this.localCouponCode = couponCode;
  }

  public onNext() {
    if (!this.isSubmitBlocked) {
      this.$emit('on-next');
    }

    // block submit button during 3s to prevent spam
    this.isSubmitBlocked = true;
    this.unblockSubmitTimeout = setTimeout(() => {
      this.isSubmitBlocked = false;
    }, 300);
  }

  public async applyCouponCode() {
    this.$validator.errors.remove('CouponCode');
    const isValid = this.localCouponCode && (await this.$api.verifyCouponCode(this.event.id, this.localCouponCode));

    if (isValid) {
      this.$emit('coupon-code-update', this.localCouponCode);
    } else {
      this.$validator.errors.add({
        field: 'CouponCode',
        msg: this.$t('shared.error.server_validation.coupon_code_invalid') as string,
      });
    }
  }

  public hasVat0TaxRule(items: QuoteItem[]) {
    return items.some(i => i.taxRuleAsString === TaxRules.Vat0);
  }

  public hasVatReverseCharge0TaxRule(items: QuoteItem[]) {
    return items.some(i => i.taxRuleAsString === TaxRules.VatReverseCharge0);
  }
}
